// --- Constants ----------------------------------------------------------------------------------

/**
 * The shorter side every picture is scaled down to. The menu renders a product photo cover-cropped —
 * 100px on the card, 440px in the popup — so the short side is the one that has to hold up, and 750
 * leaves room for a retina screen with nothing to spare.
 */
const TARGET_SHORT_SIDE = 750;

const JPEG_QUALITY = 0.8;

/** Under this a picture already uploads fine, and a round trip through canvas would only cost quality. */
const SKIP_UNDER_BYTES = 300 * 1024;

/** Re-encoding these to JPEG destroys them — an animation keeps only its first frame, a vector rasterises. */
const KEEP_AS_IS = ["image/gif", "image/svg+xml"];

// --- Helpers ------------------------------------------------------------------------------------

/**
 * Decoded through an `<img>` rather than `createImageBitmap` on purpose: a phone held upright writes
 * EXIF orientation 6 instead of rotating the pixels, browsers apply that to an `<img>` on their own,
 * and `createImageBitmap` needs an options bag that older Safari accepts and then ignores — which
 * saves every portrait photo on its side.
 */
async function decodeImage(file: File): Promise<HTMLImageElement> {
    const url = URL.createObjectURL(file);
    try {
        const image = new Image();
        image.src = url;
        await image.decode();
        return image;
    } finally {
        URL.revokeObjectURL(url);
    }
}

const toJpegName = (name: string): string => `${name.replace(/\.[^.]+$/, "") || "picture"}.jpg`;

// --- Logic --------------------------------------------------------------------------------------

/**
 * Shrinks a picked photo to something a phone can actually upload. A camera capture is a full-sensor
 * JPEG — several megabytes — which is slow on mobile data, larger than the API's proxy accepts, and
 * far past anything the menu ever renders.
 *
 * Anything that is not a raster photo, is already small, or comes out no smaller after the round trip
 * is handed back untouched, and every failure returns the original: a picture that cannot be shrunk
 * must still be a picture, never a lost save.
 */
export async function downscaleImage(file: File): Promise<File> {
    if (!file.type.startsWith("image/") || KEEP_AS_IS.includes(file.type)) return file;
    if (file.size <= SKIP_UNDER_BYTES) return file;

    try {
        const image = await decodeImage(file);
        const sourceWidth = image.naturalWidth;
        const sourceHeight = image.naturalHeight;
        if (!sourceWidth || !sourceHeight) return file;

        // Never upscale — a photo already below the target only loses by being redrawn larger
        const scale = Math.min(1, TARGET_SHORT_SIDE / Math.min(sourceWidth, sourceHeight));
        const width = Math.round(sourceWidth * scale);
        const height = Math.round(sourceHeight * scale);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext("2d");
        if (!context) return file;
        // JPEG carries no alpha, and an unpainted canvas would turn a transparent PNG's background black
        context.fillStyle = "#FFFFFF";
        context.fillRect(0, 0, width, height);
        context.drawImage(image, 0, 0, width, height);

        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
        if (!blob || blob.size >= file.size) return file;

        return new File([blob], toJpegName(file.name), { type: "image/jpeg", lastModified: Date.now() });
    } catch {
        return file;
    }
}
